<template>
  <header
    class="header bg-EXPONDO-white flex flex-col lg:z-50"
  >
    <div
      class="px-5 lg:px-16 py-2 lg:py-2.5 text-EXPONDO-primary flex w-full
            justify-between items-center flex-wrap lg:flex-nowrap gap-x-1 gap-y-2"
    >
      <div class="left-block flex gap-4 order-0 min-w-fit items-center">
        <NavMainBarAllCategoriesButton
          v-if="!isLoginPage"
          class="lg:hidden"
        />
        <SimpleLink
          class="flex flex-shrink-0"
          data-testid="app-header-url_logo"
          :to="homePath"
          @link-click="handleLogoClick"
        >
          <img
            class="w-20 lg:w-28 h-auto mt-1 lg:mt-0"
            :src="EXPONDO_LOGO_DARK_SIMPLE_PATH"
            alt="Expondo Logo"
            :aria-label="$t('expondo Home Page')"
          >
        </SimpleLink>
      </div>
      <div
        class="lg:flex middle-block header__content lg:px-6 lg:items-center justify-center order-6 lg:order-3 w-full"
        :class="[
          isSearchBarOpen ? 'flex' : 'flex xs:hidden'
        ]"
      >
        <CtSearchBar
          v-if="isCtProductSearchEnabled"
          data-testid="searchBar"
          class="header__search-bar"
          :language-and-country="languageAndCountry"
          @product-clicked="onProductClicked"
          @search-results-shown="callViewHitsEvent"
          @search-bar-clicked="hideNavigation"
          @search="searchExecuted"
        />
        <SearchBar
          v-else
          data-testid="searchBar"
          class="header__search-bar"
          :language-and-country="languageAndCountry"
          :main-search-index="mainSearchIndex"
          @product-clicked="onProductClicked"
          @search-results-shown="callViewHitsEvent"
          @search-bar-clicked="hideNavigation"
          @search="searchExecuted"
        />
      </div>
      <div class="right-block flex justify-end order-5 lg:gap-2">
        <TopMenuLanguageSelector
          v-if="isMultiLanguage"
          @language-switcher-clicked="hideNavigation"
        />
        <SfButton
          class="ex-button--pure w-10 max-xs:hidden lg:hidden"
          :class="{'hidden': isSearchBarOpen}"
          :aria-label="$t('Open search bar')"
          @click="toggleSearchBar"
        >
          <span class="iconify svg--search text-lg relative text-EXPONDO-primary flex items-center" />
        </SfButton>

        <WebformDropdown @open-contact-form="openContactForm" />
        <MyAccountDropdown
          @handle-account-click="handleAccountClick"
          @hide-navigation="hideNavigation"
        />

        <div
          class="relative flex"
        >
          <CartIcon
            v-if="!isThankYouPage"
            class="w-10 lg:w-auto"
            :is-cart-with-dot="true"
            @click="handleCartIconClick"
          />
          <CustomHint
            class="top-[120%] right-0"
            :text="$t('Your cart is empty')"
            :is-visible="isBoxVisible"
          />
        </div>
      </div>
    </div>
    <NavMainBar
      v-if="categories.length"
      :categories="categories"
      :product="product"
      :component-caching="componentCaching"
    />
    <LazySalesforceWebform
      v-if="isWebformModalOpen"
      v-bind="webformState"
      @close="closeWebformModal"
    />
  </header>
</template>

<script lang="ts">

import type { HitResultItem } from "vue-instantsearch/vue3/es";
import { cartGetters } from '~/utils/ct';
import type { Category } from '~~/types/commercetools';

import { ROUTES } from '~~/constants/routes';
import startDatadogUser from '~~/helpers/logging/startDatadogUser';
import { SCHEMA_ITEM_TYPE } from '~~/constants/seo';
import { i18nToLanguageAndCountry } from '~~/helpers/locales/i18nCurrentDetails';
import { EXPONDO_LOGO_DARK_PATH, EXPONDO_LOGO_DARK_SIMPLE_PATH } from '~~/constants/expondoLogoPath';
import { getTotalGrossPrice } from '~~/helpers/cart/getTotalPricesModule';
import type { SalesforceFormIndicatorValue } from '~~/types/salesforce';
import type { ProductVariantExtended } from "~~/types/product/ProductVariantExtended";

export default defineComponent({

  props: {
    categories: {
      type: Array as PropType<Category[]>,
      default: () => {

        return [];

      },
    },
    componentCaching: {
      type: String,
      default: '',
    },
    product: {
      type: Object as PropType<ProductVariantExtended | null>,
      default: () => {

        return null;

      },
    },
  },
  setup() {

    const { languageAndCountry, countryLocalization } = useLocalization();
    const { isCtProductSearchEnabled } = useIsCtProductSearchEnabled();
    const i18n = useI18n();

    useHead(() => ({
      htmlAttrs: {
        lang: i18nToLanguageAndCountry(i18n),
        itemscope: 'itemscope',
        itemtype: SCHEMA_ITEM_TYPE.WEB_PAGE,
      },
    }));
    const { toggleCartSidebarWithDelay } = useCartSidebars();
    const { closeCartSidebar } = useCartSidebarStore();
    const { mainSearchIndex } = useTypesense();
    const router = useRouter();
    const route = useRoute();
    const { util: ciaUtil } = useCia();
    const { trackSearchResults } = useGtmEvents();
    const { getAdjustedSlug } = useRouteExtended();
    const { isThankYouPage, isLoginPage } = useIsPage();
    const { toggleNavigation, isNavigationOpen } = useNavigationState();
    const { callViewHitsEvent, callClickHitEvent } = useTypesenseGtmEvents();
    const { isMounted } = useIsMounted();
    const { toggleHintVisibilityComputed } = useHint();
    const { isWebformModalOpen, webformState, openWebformModal, closeWebformModal } = useWebformModalStore();

    const isBoxVisible = ref(false);
    const toggleHintVisibility = () => {

      toggleHintVisibilityComputed(isBoxVisible);

    };

    const { cart } = useCartExtended();
    const searchPageUrl = getAdjustedSlug(ROUTES.SEARCH);
    const isSearchBarOpen = ref(false);
    const cartTotalPrice = computed(() => getTotalGrossPrice(cart.value));
    const totalItems = computed(() => cartGetters.getTotalItems(cart.value));
    const isMultiLanguage = computed(() => countryLocalization.value?.isMultiLanguage);
    const toggleSearchBar = () => {

      isSearchBarOpen.value = !isSearchBarOpen.value;

    };

    const openContactForm = (reason?: SalesforceFormIndicatorValue): void => {

      hideNavigation();

      openWebformModal({ preselectedReason: reason });

    };

    const handleAccountClick = () => {

      hideNavigation();

      router.push(getAdjustedSlug(ROUTES.LOGIN));

    };

    watch(() => route?.path, () => {

      closeCartSidebar();

    });

    const hideNavigation = () => {

      if (isNavigationOpen.value) {

        toggleNavigation();

      }

    };

    const handleCartIconClick = function() {

      if (isMounted.value) {

        if (totalItems.value > 0) {

          hideNavigation();
          toggleCartSidebarWithDelay();

        } else {

          toggleHintVisibility();

        }

      }

    };

    const handleLogoClick = () => {

      hideNavigation();
      router.push(getAdjustedSlug(ROUTES.HOME));

    };

    const onProductClicked = ({ product, searchTerm }: {product: HitResultItem, searchTerm: string}) => {

      callClickHitEvent(product, searchTerm);

    };

    onMounted(() => {

      ciaUtil.initTracker();
      startDatadogUser();

    });

    const searchExecuted = (currentValue) => {

      if (currentValue !== '') {

        trackSearchResults(currentValue);

      }

    };

    return {
      totalItems,
      cartTotalPrice,
      handleAccountClick,
      searchPageUrl,
      toggleNavigation,
      isNavigationOpen,
      homePath: getAdjustedSlug(ROUTES.HOME),
      EXPONDO_LOGO_DARK_PATH,
      EXPONDO_LOGO_DARK_SIMPLE_PATH,
      isSearchBarOpen,
      toggleSearchBar,
      handleCartIconClick,
      languageAndCountry,
      mainSearchIndex,
      isCtProductSearchEnabled,
      callViewHitsEvent,
      callClickHitEvent,
      hideNavigation,
      handleLogoClick,
      isMultiLanguage,
      isThankYouPage,
      onProductClicked,
      searchExecuted,
      isWebformModalOpen,
      webformState,
      closeWebformModal,
      openContactForm,
      toggleHintVisibility,
      isBoxVisible,
      isLoginPage,
    };

  },
});

</script>

<style lang="scss" scoped>
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
.header {
  position: relative;

  &__item {
    margin-left: var(--spacer-2xs);

    &-menu {
      color: var(--c-white);
      border: none;
      background-color: transparent;
      cursor: pointer;
    }
  }

  .header__search-bar {
    height: var(--search-bar-mobile-height);

    @include breakpoints.for-desktop {
      height: var(--search-bar-height);
    }
  }
}

.sf-price__regular {
  margin-left: var(--spacer-xs);
  align-self: flex-start;
  font: inherit;
}

</style>
